import { z } from 'zod'
import { RiskClassSchema } from './enums.ts'
import { MessageParamsSchema } from './messages/kinds.ts'
import { SystemAccessInputSchema, SystemAccessViewSchema } from './sharing.ts'

/**
 * MCP server registry and capability catalogue, as the admin API exposes them.
 *
 * Two things in here are policy rather than plumbing and are worth stating up front.
 *
 * **A token never appears in this file.** `tokenRef` is an indirection (`env:NAME` or
 * `secret:NAME`) resolved at call time and nowhere else, so a registry listing, an audit
 * export and a database dump all carry the *name* of a credential and never the credential.
 *
 * **`risk` is administrator-assigned.** The server's own annotations are untrusted metadata
 * (PRD-04 MCP-007), so a discovered capability starts at `privileged` — which requires human
 * approval — and only a person can lower it.
 */

/**
 * The protocol revision this platform speaks.
 *
 * 2026-07-28 is a breaking rewrite of everything older: no `initialize` handshake, no
 * protocol sessions, `server/discover` as a mandatory RPC, and protocol version plus client
 * capabilities carried in `_meta` on every request. Code written against 2025-x revisions
 * does not interoperate, so this is a constant rather than a negotiated range.
 */
export const MCP_PROTOCOL_VERSION = '2026-07-28'

/** stdio is deliberately absent: MCP-003 confines local processes to managed sandboxes. */
export const McpTransportSchema = z.enum(['streamable_http'])
export type McpTransport = z.infer<typeof McpTransportSchema>

/**
 * OAuth is optional in the specification and, inside the firewall, a static credential is
 * the sanctioned path: there is no browser in the loop to complete an authorization-code
 * flow for a service-to-service call.
 */
export const McpAuthKindSchema = z.enum(['none', 'bearer'])
export type McpAuthKind = z.infer<typeof McpAuthKindSchema>

/**
 * Whose authority a call runs under.
 *
 * One per server, never per capability (PRD-23 D-6): mixing identities inside one server
 * makes "whose authority was this?" answerable only per call, and makes an approval digest
 * that binds identity meaningless.
 */
export const McpConnectionModeSchema = z.enum(['open', 'shared_secret', 'service_account', 'delegated'])
export type McpConnectionMode = z.infer<typeof McpConnectionModeSchema>

export const McpTrustTierSchema = z.enum(['untrusted', 'reviewed', 'first_party'])
export type McpTrustTier = z.infer<typeof McpTrustTierSchema>

export const McpCapabilityKindSchema = z.enum(['tool', 'resource', 'prompt'])
export type McpCapabilityKind = z.infer<typeof McpCapabilityKindSchema>

export const TOKEN_REF_PATTERN = /^(env|secret):[A-Za-z_][A-Za-z0-9_.-]{0,127}$/
export const TokenRefSchema = z
  .string()
  .regex(TOKEN_REF_PATTERN, 'a token reference must be "env:VAR_NAME" or "secret:NAME", never a literal token')

/**
 * Server names prefix every qualified capability id, and a qualified id has to survive
 * normalisation into the `^[a-zA-Z0-9_-]{1,64}$` a model provider accepts for a tool name.
 * Constraining the name here keeps that mapping readable instead of hash-suffixed.
 */
export const MCP_SERVER_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{1,30}$/
export const McpServerNameSchema = z
  .string()
  .regex(MCP_SERVER_NAME_PATTERN, 'server name must be lowercase alphanumeric with dashes, 2-31 characters')

export const McpServerSchema = z.object({
  id: z.uuid(),
  name: McpServerNameSchema,
  description: z.string(),
  url: z.string(),
  transport: McpTransportSchema,
  authKind: McpAuthKindSchema,
  /** The reference, never the secret. Null when `authKind` is `none`. */
  tokenRef: z.string().nullable(),
  trustTier: McpTrustTierSchema,
  enabled: z.boolean(),
  /**
   * Who can use it (PRD-26 D-25): everyone, or specific groups. `groups` with none left reaches
   * nobody. Enforced where tools are offered to a run, in the builder's list and at re-validation.
   */
  access: SystemAccessViewSchema,
  protocolVersion: z.string().nullable(),
  /** `modern` carries metadata per request; `legacy` handshakes and holds a session. */
  era: z.enum(['modern', 'legacy']).nullable(),
  supportedVersions: z.array(z.string()),
  serverInfo: z.record(z.string(), z.unknown()),
  /** The capability classes the server says it has. Read to report what was not imported. */
  capabilities: z.record(z.string(), z.unknown()),
  /**
   * Free text the server offers about how to use it.
   *
   * Shown to an administrator and never given to any agent: by its own definition it is
   * natural-language guidance aimed at a model, written by a party this platform classifies
   * as untrusted (PRD-23 CNX-079).
   */
  instructions: z.string(),
  /** Whose authority a call runs under. One per server. */
  connectionMode: McpConnectionModeSchema,
  lastProbeAt: z.iso.datetime().nullable(),
  lastProbeStatus: z.string().nullable(),
  lastProbeDetail: z.string().nullable(),
  capabilityCount: z.number().int().nonnegative(),
  /**
   * How many of those are actually callable — enabled, not quarantined, not withdrawn.
   *
   * Separate from the total because they answer different questions, and a surface that had
   * only the total was reduced to reporting "2 of 2 switched on" about two capabilities that
   * were both held back. A count that flatters is worse than no count.
   */
  liveCapabilityCount: z.number().int().nonnegative(),
  version: z.number().int(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime()
})
export type McpServer = z.infer<typeof McpServerSchema>

export const CreateMcpServerRequestSchema = z
  .object({
    name: McpServerNameSchema,
    description: z.string().max(2000).default(''),
    url: z.url(),
    transport: McpTransportSchema.default('streamable_http'),
    authKind: McpAuthKindSchema.default('none'),
    tokenRef: TokenRefSchema.nullish(),
    trustTier: McpTrustTierSchema.default('untrusted'),
    enabled: z.boolean().default(true),
    access: SystemAccessInputSchema.default({ kind: 'everyone', groupIds: [] })
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.authKind === 'bearer' && !value.tokenRef) {
      ctx.addIssue({
        code: 'custom',
        path: ['tokenRef'],
        message: 'bearer authentication requires a tokenRef ("env:NAME" or "secret:NAME")'
      })
    }
  })
export type CreateMcpServerRequest = z.infer<typeof CreateMcpServerRequestSchema>

export const UpdateMcpServerRequestSchema = z
  .object({
    description: z.string().max(2000).optional(),
    url: z.url().optional(),
    authKind: McpAuthKindSchema.optional(),
    /** Explicit null clears the reference; omitted leaves it untouched. */
    tokenRef: TokenRefSchema.nullish(),
    trustTier: McpTrustTierSchema.optional(),
    enabled: z.boolean().optional(),
    access: SystemAccessInputSchema.optional()
  })
  .strict()
export type UpdateMcpServerRequest = z.infer<typeof UpdateMcpServerRequestSchema>

/**
 * What a server claims about how one of its tools behaves.
 *
 * Every field is the server's word and none is a permission. `null` is preserved and means
 * the server said nothing — a different thing from saying no, because the specification's
 * defaults for the two dangerous hints are pessimistic and folding absence into `false`
 * would quietly invert both.
 */
export const McpToolAnnotationsSchema = z.object({
  title: z.string().nullable(),
  readOnlyHint: z.boolean().nullable(),
  destructiveHint: z.boolean().nullable(),
  idempotentHint: z.boolean().nullable(),
  openWorldHint: z.boolean().nullable()
})
export type McpToolAnnotations = z.infer<typeof McpToolAnnotationsSchema>

/**
 * The platform's proposal for a capability's class, and the sentence that justifies it.
 *
 * Carried beside the capability rather than folded into `risk`, which is deliberate: `risk`
 * is what the platform enforces and a human set, and a proposal that wrote itself into that
 * field would be the server setting policy through the back door.
 */
export const RiskSuggestionSchema = z.object({
  risk: RiskClassSchema,
  because: z.string(),
  /** False when the trust tier refused to let any claim move it. */
  fromAnnotations: z.boolean()
})
export type RiskSuggestion = z.infer<typeof RiskSuggestionSchema>

/**
 * Why a capability is being held back — the structural half of `quarantineReason` (PRD-27 D-6).
 *
 * Two causes, because they are two events and an administrator should conclude different things
 * from them. `re_review` is the platform having learned to record something it did not record
 * before: nothing about the tool moved, and on the first discovery after a digest-recipe change
 * every capability lands in the queue at once. `contract_changed` is a remote party altering a
 * tool somebody already judged, which is the rug-pull the quarantine exists to catch.
 *
 * `null` is possible although the migration backfilled every row it found: during a rolling
 * deploy an old pod can still quarantine a capability and write no cause. The review screen
 * reads `null` as the conservative event -- calling a re-review "the system changed this"
 * over-warns, and the other way round under-warns about a rug-pull.
 */
export const McpQuarantineCauseSchema = z.enum(['re_review', 'contract_changed'])
export type McpQuarantineCause = z.infer<typeof McpQuarantineCauseSchema>

export const McpCapabilitySchema = z.object({
  id: z.uuid(),
  serverId: z.uuid(),
  serverName: z.string(),
  kind: McpCapabilityKindSchema,
  /** The name the server knows it by; what goes on the wire in `tools/call`. */
  remoteName: z.string(),
  /** `server/tool`. Stable across servers offering the same bare name. */
  qualifiedName: z.string(),
  /** The name the model is offered, normalised to the provider's tool-name charset. */
  toolName: z.string(),
  title: z.string(),
  description: z.string(),
  inputSchema: z.record(z.string(), z.unknown()),
  outputSchema: z.record(z.string(), z.unknown()).nullable(),
  inputDigest: z.string(),
  /** The server's untrusted claims. Rendered as a quotation, never as a control's value. */
  annotations: McpToolAnnotationsSchema.nullable(),
  /** A proposal, present only where it differs from what the row already holds. */
  suggestion: RiskSuggestionSchema.nullable(),
  /** Set when this client refuses to carry the server's definition, with the reason. */
  rejectedReason: z.string().nullable(),
  risk: RiskClassSchema,
  enabled: z.boolean(),
  quarantinedAt: z.iso.datetime().nullable(),
  /**
   * The operator's detail: which digests, and when. Kept permanently and never parsed -- the
   * cause below is what anything branches on.
   */
  quarantineReason: z.string().nullable(),
  quarantineCause: McpQuarantineCauseSchema.nullable(),
  firstSeenAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  removedAt: z.iso.datetime().nullable()
})
export type McpCapability = z.infer<typeof McpCapabilitySchema>

export const UpdateMcpCapabilityRequestSchema = z
  .object({
    enabled: z.boolean().optional(),
    risk: RiskClassSchema.optional(),
    /**
     * Clearing a quarantine is a separate, explicit act from enabling: an operator has to
     * say "I reviewed the new schema" and "I want this callable again" as two decisions,
     * because the first is a security judgement and the second is a product one.
     */
    clearQuarantine: z.boolean().optional()
  })
  .strict()
export type UpdateMcpCapabilityRequest = z.infer<typeof UpdateMcpCapabilityRequestSchema>

/**
 * Where a preflight stopped.
 *
 * Ordered as the probe runs them, and deliberately granular: "it doesn't work" is answered
 * by naming the stage, so an operator can tell a bad URL from a missing credential from an
 * unreachable host from a server that speaks a different protocol (MCP-025).
 */
export const McpProbeStageSchema = z.enum([
  'url_invalid',
  'ssrf_blocked',
  'token_unresolved',
  'connect',
  /** The endpoint answered, and no revision it speaks is one this platform speaks. */
  'version_mismatch',
  /** The endpoint answered `401`, naming the authority that issues its credentials. */
  'authorization_required',
  'discover',
  'tools_list'
])
export type McpProbeStage = z.infer<typeof McpProbeStageSchema>

export const McpProbeResultSchema = z.object({
  ok: z.boolean(),
  /** Null on success; otherwise the stage that failed. */
  stage: McpProbeStageSchema.nullable(),
  /**
   * What happened, in the transport's own words — a status line, a TLS error, a parser's
   * complaint about a body nobody controls.
   *
   * Deliberately NOT keyed, and it is the one record in this cluster that stays that way
   * (PRD-27 D-4). Its content is whatever the far side or the runtime said, so there is no
   * bounded space of sentences to enumerate; inventing a key for it would mean either
   * throwing the specifics away or shipping a key that renders a different sentence every
   * time. An honest passthrough beats a dishonest key. It is also the line an operator quotes
   * to the system's owner, which is a reason for it to stay verbatim rather than a reason to
   * translate it.
   */
  detail: z.string(),
  /** What to change. Empty on success. */
  hint: z.string(),
  /**
   * Which hint this is, so an administrator reads it in their own language (PRD-27 D-4).
   *
   * Named for the field it identifies rather than `messageKey`, because this record carries
   * two prose fields and only one of them is renderable: `detail` is the far side's own words
   * and stays English forever (above), `hint` is ours and is one of sixteen sentences.
   *
   * `stage` is not that identifier, for the usual reason: `url_invalid` alone is five
   * different fixes — not a URL, credentials in the URL, plaintext, an unsupported transport,
   * a hostname that does not resolve — and one sentence covering all five tells nobody which
   * of the five they have.
   *
   * Nullish so a client older than a hint key still renders the English `hint`, which is on
   * the wire for exactly that reason and is never removed.
   */
  hintKey: z.string().nullish(),
  /** What the hint interpolates. Values only — never a pre-built phrase (D-20). */
  hintParams: MessageParamsSchema.nullish(),
  protocolVersion: z.string().nullable(),
  /** `modern` speaks per-request metadata; `legacy` handshakes and carries a session. */
  era: z.enum(['modern', 'legacy']).nullable(),
  /** Every revision the server named, whether or not this platform speaks it. */
  supportedVersions: z.array(z.string()),
  serverInfo: z.record(z.string(), z.unknown()),
  /** The capability classes the server says it has: tools, resources, prompts. */
  capabilities: z.record(z.string(), z.unknown()),
  /** Free text the server offers about itself. Untrusted; never placed in a prompt. */
  instructions: z.string(),
  /**
   * Set when the stage is `authorization_required`: the RFC 9728 document that names the
   * authority issuing this server's credentials. It is the entry point to the whole
   * authorization flow, so it is carried rather than folded into a message.
   */
  authorization: z.object({ resourceMetadataUrl: z.string().nullable(), scope: z.string().nullable() }).nullable(),
  /**
   * Whether the platform sent a credential on this attempt.
   *
   * The diagnostics cannot infer it: a successful probe looks identical whether the system
   * wanted nothing or accepted a bearer token, and the surface guessed "none needed" for
   * both — which reads to an administrator as permission to delete a token reference that
   * is load-bearing. Never says *which* credential; that is `tokenRef` on the server row.
   */
  credentialSupplied: z.boolean(),
  toolCount: z.number().int().nonnegative(),
  latencyMs: z.number().int().nonnegative()
})
export type McpProbeResult = z.infer<typeof McpProbeResultSchema>

export const McpDiscoveryChangeSchema = z.object({
  qualifiedName: z.string(),
  detail: z.string()
})

export const McpDiscoveryResultSchema = z.object({
  serverId: z.uuid(),
  serverName: z.string(),
  protocolVersion: z.string().nullable(),
  added: z.array(McpDiscoveryChangeSchema),
  /** Schema drift. Each of these is now quarantined and not callable. */
  quarantined: z.array(McpDiscoveryChangeSchema),
  removed: z.array(McpDiscoveryChangeSchema),
  unchanged: z.number().int().nonnegative(),
  /** A qualified name already owned by a different server; not imported. */
  collisions: z.array(McpDiscoveryChangeSchema),
  /**
   * Offered by the server and refused by this client, with the reason.
   *
   * Reported rather than dropped silently: a tool missing from the catalogue with no
   * explanation is indistinguishable from a server that never offered it, and the
   * administrator is the only person who can ask the server's owner to fix it.
   */
  rejected: z.array(McpDiscoveryChangeSchema),
  /**
   * Quarantined because the platform now records something it did not before, not because
   * the server changed anything.
   *
   * Separate from `quarantined` so the two are not read as one event. A drift quarantine is
   * a server doing something and is worth alarm; this one is the platform catching up, it
   * arrives for every capability at once, and presenting it as drift is how a review queue
   * teaches people that quarantines are noise.
   */
  needsReReview: z.array(McpDiscoveryChangeSchema)
})
export type McpDiscoveryResult = z.infer<typeof McpDiscoveryResultSchema>

export const McpServerListResponseSchema = z.object({ items: z.array(McpServerSchema) })
export const McpCapabilityListResponseSchema = z.object({ items: z.array(McpCapabilitySchema) })
